import CheckoutFooter from '@checkout/components/CheckoutFooter';
import SummaryScrim from '@checkout/components/SummaryScrim';
import ThemeToggle from '@checkout/components/ThemeToggle';
import { useSummaryOverlaySpacing } from '@checkout/hooks/useSummaryOverlaySpacing';
import Placeholder from '@components/Placeholder';
import { t } from '@i18n';

import '@checkout/components/CheckoutSummary/styles.scss';
import '@checkout/components/PlanCard/styles.scss';
import '@checkout/components/ThemeToggle/styles.scss';
import '@checkout/components/CheckoutFooter/styles.scss';
import '@checkout/components/SummaryScrim/styles.scss';
import './CheckoutPageView.scss';
import './CheckoutSkeletonView.scss';

// One bone row per plan. payload is empty while this renders, so the real count is unknowable;
// three is the count that fills the list without scrolling at the common viewport heights.
const PLAN_ROWS = [0, 1, 2];

/**
 * The loading state renders the loaded page's own DOM — same classes, same stylesheet — with only
 * the text and image leaves swapped for bones. Geometry is therefore inherited rather than copied,
 * which is the whole point: the previous skeleton hand-copied a layout that was later redesigned,
 * and every box on the page moved when the payload arrived.
 *
 * What it cannot mirror is what the payload alone knows: the community name and banner, the plan
 * count and prices, and which payment provider will render. Those are reserved at their structural
 * minimum rather than guessed, so the page only ever grows downward into them.
 */
export default function CheckoutSkeletonView() {
  const { panelRef, headerRef, footerRef } = useSummaryOverlaySpacing([]);

  return (
    <div className='checkout-shell checkout-shell--skeleton' aria-busy='true'>
      <span role='status' className='checkout-skeleton-status'>{t('app.loading')}</span>

      <aside ref={panelRef} className='checkout-summary-panel'>
        {/* no .checkout-summary-bg — the banner is a payload field, and it paints behind the
            content at inset:-56px, so its late arrival costs no layout */}
        <div className='checkout-summary-overlay' />

        <div ref={headerRef} className='checkout-summary-header'>
          <SummaryScrim />

          <div className='checkout-summary-brand'>
            <div className='checkout-summary-avatar'>
              <Placeholder w='100%' h='100%' radius='0' />
            </div>

            <div className='checkout-summary-brand-copy'>
              <Placeholder className='checkout-eyebrow' w={90} />
              <Placeholder as='h1' w='70%' />
            </div>

            <ThemeToggle />
          </div>

          <div className='checkout-plans-header'>
            <div>
              <Placeholder className='checkout-eyebrow' w={120} />
              <Placeholder as='h2' w={180} />
            </div>
          </div>
        </div>

        {/* mobile-only bone for the selected-plan trigger row (hidden on desktop by CSS) */}
        <button type='button' className='checkout-plan-option checkout-plan-trigger' disabled>
          <span className='checkout-plan-option-banner'>
            <Placeholder w='100%' h='100%' radius='0' />
          </span>

          <span className='checkout-plan-option-content'>
            <Placeholder className='checkout-plan-option-title' w='62%' />
            <Placeholder className='checkout-plan-option-price' w='44%' />
          </span>

          <span className='checkout-plan-option-marker' />
        </button>

        <div className='checkout-plans-list' aria-hidden='true'>
          {PLAN_ROWS.map((row) => (
            <button key={row} type='button' className='checkout-plan-option' disabled>
              <span className='checkout-plan-option-banner'>
                <Placeholder w='100%' h='100%' radius='0' />
              </span>

              <span className='checkout-plan-option-content'>
                <Placeholder className='checkout-plan-option-title' w='62%' />
                <Placeholder className='checkout-plan-option-price' w='44%' />
              </span>

              <span className='checkout-plan-option-marker' />
            </button>
          ))}
        </div>

        <div ref={footerRef} className='checkout-summary-footer'>
          <SummaryScrim />

          {/* the installment prefix, the total line and the coupon chip are all conditional, so the
              card reserves only the header and the price row it always renders */}
          <div className='checkout-plan-card' aria-hidden='true'>
            <div className='checkout-plan-card-header'>
              <div>
                <Placeholder className='checkout-eyebrow' w={80} />
                <Placeholder as='h2' w={150} />
              </div>

              <div className='checkout-plan-icon'>
                <Placeholder w={20} h={20} radius='var(--radius-xs)' />
              </div>
            </div>

            <div className='checkout-price-block'>
              <div className='checkout-price-row'>
                <Placeholder className='checkout-price-amount' w={150} />
              </div>
            </div>
          </div>
        </div>
      </aside>

      <main className='checkout-form-panel'>
        <div className='checkout-page'>
          <div className='checkout-form-topbar'>
            <Placeholder className='checkout-eyebrow' w={80} />
            {/* the country pill's own min-height, which no breakpoint overrides */}
            <Placeholder w={112} h={42} radius='var(--radius-md)' />
          </div>

          <div className='checkout-form-title'>
            {/* the static title heading; the greeting above it only exists once a name is typed */}
            <Placeholder w='70%' h='var(--line-height-heading)' />
            <Placeholder as='p' w='92%' />
          </div>

          <Placeholder as='h5' className='mt-3' w='42%' />
          <div className='input-group'>
            <div className='checkout-skeleton-field'><Placeholder w='52%' h={14} /></div>
            <div className='checkout-skeleton-field'><Placeholder w='68%' h={14} /></div>
            <div className='checkout-skeleton-field'><Placeholder w='44%' h={14} /></div>
          </div>

          {/* Below the contact block the two providers diverge, and which one renders depends on the
              payload. Only the spine they share is reserved; an Asaas checkout grows downward into
              the space beneath it rather than collapsing upward. */}
          <div className='input-group mt-3'>
            <div className='checkout-skeleton-field checkout-skeleton-field--coupon'>
              <Placeholder w='40%' h={14} />
              <Placeholder w={32} h={32} radius='var(--radius-md)' />
            </div>
          </div>

          <Placeholder as='h5' className='mt-3' w='38%' />
          <div className='input-group'>
            <div className='checkout-skeleton-field checkout-skeleton-field--card'>
              <Placeholder w='58%' h={14} />
            </div>
            <div className='checkout-skeleton-field checkout-skeleton-field--card'>
              <Placeholder w='34%' h={14} />
            </div>
          </div>

          <Placeholder className='mt-4' w='100%' h={48} radius='var(--radius-lg)' />

          <CheckoutFooter />
        </div>
      </main>
    </div>
  );
}
